import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GroomingCandidateMode, ProductAreaSource } from "@ttc-spectree/toolbox-contract";
import type { FogbugzProjectLinkInput } from "@ttc-spectree/toolbox-contract/fogbugz";
import {
  findOpenAgenda,
  groomingApi,
  type GroomingBacklogSort,
  type GroomingBacklogTop,
  type GroomingBatch,
  type GroomingConfigInput,
  type GroomingMeetingPattern,
  type NextAgendaResult,
  type ProductAreaInput,
  type UndoNextAgendaResult,
} from "@/lib/api/grooming";
import { workItemsApi, type WorkItemAddress } from "@/lib/api/work-items";
import {
  groomingBatchRootKey,
  groomingProjectKey,
  groomingRootKey,
  retryTransientOnce,
  workItemKeys,
} from "@/hooks/queries/use-work-items";

/** Query key factory for the grooming workspace API (epics PI-SPEC-SW-E279 and PI-SPEC-SW-E280). */
export const groomingKeys = {
  all: groomingRootKey,
  projects: () => [...groomingKeys.all, "projects"] as const,
  project: (project: string) => groomingProjectKey(project),
  config: (project: string) => [...groomingKeys.project(project), "config"] as const,
  productAreas: (project: string) => [...groomingKeys.project(project), "product-areas"] as const,
  /** PKG-PM-F: the time zones the project's role holders use (the meeting schedule's zone picker). */
  teamTimeZones: (project: string) => [...groomingKeys.project(project), "team-time-zones"] as const,
  backlog: (project: string, top: GroomingBacklogTop, sort: GroomingBacklogSort) =>
    [...groomingKeys.project(project), "backlog", String(top), sort] as const,
  batches: (project: string) => [...groomingKeys.project(project), "batches"] as const,
  batch: (batchId: string) => [...groomingBatchRootKey, batchId] as const,
  candidates: (project: string, mode: GroomingCandidateMode) =>
    [...groomingKeys.project(project), "candidates", mode] as const,
  commentPreview: (briefId: string) => [...groomingKeys.all, "comment-preview", briefId] as const,
  /**
   * E293: "How grooming is going" over `sinceDays`. Under the project key, so
   * every invalidation of the project's reads refreshes it.
   */
  metrics: (project: string, sinceDays: number) => [...groomingKeys.project(project), "metrics", sinceDays] as const,
  /** E280: the next session (shared agenda, meeting date, typical time). Under the project key. */
  session: (project: string) => [...groomingKeys.project(project), "session"] as const,
  /**
   * E288: the ADO repositories the Admin picks from, owned by `owner` (the
   * configured project when omitted). Deliberately NOT under the project key:
   * a config save, the Grooming switch or a session start invalidates the
   * project's reads, and none of them should re-list ADO's repositories.
   */
  adoRepositoriesFor: (project: string) => [...groomingKeys.all, "ado-repositories", project.toLowerCase()] as const,
  adoRepositories: (project: string, owner?: string) =>
    [...groomingKeys.adoRepositoriesFor(project), (owner ?? project).toLowerCase()] as const,
  /** E288: one ADO repository's branches (in `owner`, the configured project when omitted). Not under the project key either. */
  adoBranches: (project: string, repositoryId: string, owner?: string) =>
    [...groomingKeys.all, "ado-branches", project.toLowerCase(), (owner ?? project).toLowerCase(), repositoryId] as const,
  /** E308: the project's FogBugz links and the overrides pointing into it. Under the project key. */
  fogbugzLinks: (project: string) => [...groomingKeys.project(project), "fogbugz-links"] as const,
  /**
   * E308: an ADO project's area paths for the link editor's picker. Not under
   * the project key, like the ADO repository lists: invalidating a project's
   * reads should not re-read ADO's area tree.
   */
  adoAreaPaths: (project: string) => [...groomingKeys.all, "ado-area-paths", project.toLowerCase()] as const,
};

/** How often a browser re-reads a running batch: it has no runner events (SSE is process-local). */
export const BATCH_POLL_INTERVAL_MS = 5000;

/** Projects with grooming set up. `enabled: false` skips the read until it is needed. */
export function useGroomingProjects(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: groomingKeys.projects(),
    queryFn: () => groomingApi.projects(),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
    retry: retryTransientOnce,
    enabled: options.enabled ?? true,
  });
}

/** A project's config (repos, team, groom tag). */
export function useGroomingConfig(project: string | undefined) {
  return useQuery({
    queryKey: groomingKeys.config(project ?? ""),
    queryFn: () => groomingApi.config(project ?? ""),
    select: (response) => response.data,
    enabled: Boolean(project),
    retry: retryTransientOnce,
  });
}

/** A project's active product areas. */
export function useProductAreas(project: string | undefined) {
  return useQuery({
    queryKey: groomingKeys.productAreas(project ?? ""),
    queryFn: () => groomingApi.productAreas(project ?? ""),
    select: (response) => response.data,
    enabled: Boolean(project),
    retry: retryTransientOnce,
  });
}

/**
 * The team's backlog: one live ADO read with the caller's PAT per fetch, so
 * it stays fresh for a minute and a client error (PAT, missing config) is
 * never retried. `sort` is required so every caller chooses its order: the
 * Backlog page follows the person's sort menu, while Session, Present mode
 * and the command palette keep the ADO backlog order (`rank`). `cacheOnly`
 * (E289, the session run page) only follows a read another page made and
 * never starts one, so it adds no ADO read.
 */
export function useGroomingBacklog(
  project: string | undefined,
  top: GroomingBacklogTop,
  sort: GroomingBacklogSort,
  options: { cacheOnly?: boolean } = {}
) {
  return useQuery({
    queryKey: groomingKeys.backlog(project ?? "", top, sort),
    queryFn: () => groomingApi.backlog(project ?? "", top, sort),
    select: (response) => response.data,
    enabled: !options.cacheOnly && Boolean(project),
    staleTime: 60 * 1000,
    retry: retryTransientOnce,
  });
}

/**
 * How grooming is going (E293, UX-PM-01): the pilot metrics over the last
 * `sinceDays` days. The Backlog disables it while its own read is in flight
 * and re-reads it after each fresh Backlog read
 * ({@link useMetricsAfterBacklogRead}), so the team points that read just
 * recorded are counted; otherwise it stays fresh for a minute. The sprint part is a live ADO read with the
 * caller's PAT, so a client error is never retried.
 */
export function useGroomingMetrics(
  project: string | undefined,
  sinceDays: number,
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: groomingKeys.metrics(project ?? "", sinceDays),
    queryFn: () => groomingApi.metrics(project ?? "", sinceDays),
    select: (response) => response.data,
    enabled: Boolean(project) && (options.enabled ?? true),
    staleTime: 60 * 1000,
    retry: retryTransientOnce,
  });
}

/**
 * Re-read How grooming is going after each fresh Backlog read (E293): that
 * read records the team points the metrics count, so metrics cached before it
 * would miss them. The first render is skipped: cached metrics were read
 * after the cached Backlog read they sit beside.
 */
export function useMetricsAfterBacklogRead(project: string | undefined, backlogUpdatedAt: number) {
  const queryClient = useQueryClient();
  const seen = useRef(backlogUpdatedAt);
  useEffect(() => {
    if (!project || backlogUpdatedAt === seen.current) return;
    seen.current = backlogUpdatedAt;
    void queryClient.invalidateQueries({ queryKey: [...groomingKeys.project(project), "metrics"] });
  }, [project, backlogUpdatedAt, queryClient]);
}

/** A project's batches, newest first (`prepare-sessions` sees everyone's; others their own). */
export function useGroomingBatches(project: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: groomingKeys.batches(project ?? ""),
    queryFn: () => groomingApi.batches(project ?? ""),
    select: (response) => response.data,
    enabled: Boolean(project) && (options.enabled ?? true),
    retry: retryTransientOnce,
  });
}

/**
 * One batch with its items. With `poll`, the batch is re-read every
 * {@link BATCH_POLL_INTERVAL_MS} while it is running: a browser has no runner
 * events, and server-sent events would not work across API replicas.
 */
export function useGroomingBatch(batchId: string | undefined, options: { poll?: boolean } = {}) {
  const poll = options.poll ?? false;
  return useQuery({
    queryKey: groomingKeys.batch(batchId ?? ""),
    queryFn: () => groomingApi.batch(batchId ?? ""),
    select: (response) => response.data,
    enabled: Boolean(batchId),
    retry: retryTransientOnce,
    refetchInterval: (query) =>
      poll && query.state.data?.data.status === "running" ? BATCH_POLL_INTERVAL_MS : false,
  });
}

/** Items for an `unestimated` or `tagged` run: eligible ones (at most 20), and the ones below the bar. */
export function useGroomingCandidates(project: string | undefined, mode: GroomingCandidateMode | null) {
  return useQuery({
    queryKey: groomingKeys.candidates(project ?? "", mode ?? "unestimated"),
    queryFn: () => groomingApi.candidates(project ?? "", { mode: mode ?? "unestimated" }),
    select: (response) => response.data,
    enabled: Boolean(project) && mode !== null,
    staleTime: 60 * 1000,
    retry: retryTransientOnce,
  });
}

/** The exact comment HTML a post of a held brief would send. No ADO write. */
export function useBriefCommentPreview(item: WorkItemAddress | null, briefId: string | null) {
  return useQuery({
    queryKey: groomingKeys.commentPreview(briefId ?? ""),
    queryFn: () =>
      item ? workItemsApi.briefCommentPreview(item, briefId ?? "") : Promise.reject(new Error("No item to preview")),
    select: (response) => response.data,
    enabled: item !== null && Boolean(briefId),
    retry: retryTransientOnce,
  });
}

/** Refresh everything a project's grooming pages read. */
function invalidateProject(queryClient: ReturnType<typeof useQueryClient>, project: string): void {
  void queryClient.invalidateQueries({ queryKey: groomingKeys.project(project) });
}

/** `owner` when it names another ADO project than `project`, else undefined (the API's default). */
function otherOwner(project: string, owner: string | undefined): string | undefined {
  return owner && owner.toLowerCase() !== project.toLowerCase() ? owner : undefined;
}

/**
 * The ADO repositories the Admin picks from on the Repos page (`manage-repos`,
 * epic PI-SPEC-SW-E288, frame 1e): those of `owner` when it is another ADO
 * project (`?project=`), else the configured project's. Read only while the
 * picker is open.
 */
export function useAdoRepositories(
  project: string | undefined,
  owner?: string,
  options: { enabled?: boolean } = {}
) {
  const other = otherOwner(project ?? "", owner);
  return useQuery({
    queryKey: groomingKeys.adoRepositories(project ?? "", other),
    queryFn: () => groomingApi.adoRepositories(project ?? "", other),
    select: (response) => response.data,
    enabled: Boolean(project) && (options.enabled ?? true),
    staleTime: 60_000,
    retry: retryTransientOnce,
  });
}

/**
 * One ADO repository's branches, the default first (`manage-repos`, E288),
 * read in `owner` (the ADO project that owns the repository) when it is not
 * the configured project. Idle until a repository is picked.
 */
export function useAdoBranches(project: string | undefined, repositoryId: string | null, owner?: string) {
  const other = otherOwner(project ?? "", owner);
  return useQuery({
    queryKey: groomingKeys.adoBranches(project ?? "", repositoryId ?? "", other),
    queryFn: () => groomingApi.adoBranches(project ?? "", repositoryId ?? "", other),
    select: (response) => response.data,
    enabled: Boolean(project) && Boolean(repositoryId),
    staleTime: 60_000,
    retry: retryTransientOnce,
  });
}

/**
 * The Grooming on/off switch (`manage-repos`, E288). Refreshes only the
 * project's config and the project list (a project switched off drops out of
 * it, so the Backlog and Session stop listing it); the backlog, the session
 * and ADO's repository lists are not re-read. Never retried.
 */
export function useSetGroomingEnabled(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => groomingApi.setEnabled(project, enabled).then((r) => r.data),
    retry: false,
    onSuccess: (config) => {
      queryClient.setQueryData(groomingKeys.config(project), { data: config });
      void queryClient.invalidateQueries({ queryKey: groomingKeys.config(project) });
      void queryClient.invalidateQueries({ queryKey: groomingKeys.projects() });
    },
  });
}

/**
 * Create or replace a project's config (`manage-repos`, Admin only since
 * E288). Refreshes only the config and the project list. ADO's repository
 * lists are marked stale without being re-read now, so the next picker that
 * opens reads fresh "already added" marks. Never retried.
 */
export function useSaveGroomingConfig(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: GroomingConfigInput) => groomingApi.saveConfig(project, input).then((r) => r.data),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groomingKeys.config(project) });
      void queryClient.invalidateQueries({ queryKey: groomingKeys.projects() });
      void queryClient.invalidateQueries({ queryKey: groomingKeys.adoRepositoriesFor(project), refetchType: "none" });
    },
  });
}

/** Add a person-written product area (`map-project`). Never retried. */
export function useCreateProductArea(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductAreaInput) => groomingApi.createProductArea(project, input).then((r) => r.data),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groomingKeys.productAreas(project) });
    },
  });
}

/** Edit a product area (`map-project`). Never retried. */
export function useUpdateProductArea(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ areaId, input }: { areaId: string; input: Partial<ProductAreaInput> }) =>
      groomingApi.updateProductArea(project, areaId, input).then((r) => r.data),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groomingKeys.productAreas(project) });
    },
  });
}

/** Archive a product area (`map-project`). Never retried. */
export function useArchiveProductArea(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (areaId: string) => groomingApi.archiveProductArea(project, areaId).then((r) => r.data),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groomingKeys.productAreas(project) });
    },
  });
}

/**
 * Undo a product-area removal (`map-project`, PKG-PM-F): `source` is the
 * source the area had before it was removed. Never retried.
 */
export function useRestoreProductArea(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ areaId, source }: { areaId: string; source: ProductAreaSource }) =>
      groomingApi.restoreProductArea(project, areaId, source).then((r) => r.data),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: groomingKeys.productAreas(project) });
    },
  });
}

/**
 * The distinct IANA time zones of the people holding a Toolbox role on the
 * project (`map-project`, PKG-PM-F), sorted. Pass undefined to skip the read.
 */
export function useTeamTimeZones(project: string | undefined) {
  return useQuery({
    queryKey: groomingKeys.teamTimeZones(project ?? ""),
    queryFn: () => groomingApi.teamTimeZones(project ?? ""),
    select: (response) => response.data,
    enabled: Boolean(project),
    staleTime: 5 * 60 * 1000,
    retry: retryTransientOnce,
  });
}

/**
 * "Add to session": add items to the caller's open draft agenda for the
 * project, or create one holding them. `manage-agenda` (the API returns 403
 * otherwise). Never retried: a retry after a timed-out create could open a
 * second agenda.
 */
export function useAddToSession(project: string, userId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemIds: string[]) => {
      const batches = await queryClient.fetchQuery({
        queryKey: groomingKeys.batches(project),
        queryFn: () => groomingApi.batches(project),
      });
      const agenda = findOpenAgenda(batches.data, userId);
      if (agenda) return (await groomingApi.addBatchItems(agenda.id, itemIds)).data;
      return (await groomingApi.createBatch({ project, mode: "agenda", itemIds })).data.batch;
    },
    retry: false,
    onSuccess: (batch) => {
      void queryClient.invalidateQueries({ queryKey: groomingKeys.batches(project) });
      void queryClient.invalidateQueries({ queryKey: groomingKeys.batch(batch.id) });
    },
  });
}

/** What to put in a run: the batch to change (or none) and the ticked items. */
export interface PrepareRunInput {
  /** The caller's open draft agenda, when there is one; it is updated instead of a new batch. */
  draftBatchId?: string;
  /** How the items were chosen. `agenda` keeps the person's own list. */
  mode: "agenda" | GroomingCandidateMode;
  /** The ticked tracker item ids, in agenda order (at most MAX_GROOMING_BATCH_ITEMS). */
  itemIds: string[];
}

/**
 * "Groom N items": make the batch hold exactly the ticked items, ready to
 * run. `agenda` updates the caller's draft agenda (or creates one);
 * `unestimated` and `tagged` create a batch the API fills from the same
 * candidates, then drop the items the person unticked. `prepare-sessions` only.
 * Never retried: a retry after a timed-out create could open a second batch.
 */
export function usePrepareGroomingRun(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PrepareRunInput): Promise<GroomingBatch> => {
      const wanted = new Set(input.itemIds);
      let batch: GroomingBatch;
      if (input.mode === "agenda") {
        if (!input.draftBatchId) {
          return (await groomingApi.createBatch({ project, mode: "agenda", itemIds: input.itemIds })).data.batch;
        }
        batch = (await groomingApi.batch(input.draftBatchId)).data;
      } else {
        batch = (await groomingApi.createBatch({ project, mode: input.mode })).data.batch;
      }
      for (const item of batch.items) {
        if (!wanted.has(item.itemId)) batch = (await groomingApi.removeBatchItem(batch.id, item.itemId)).data;
      }
      const present = new Set(batch.items.map((item) => item.itemId));
      const missing = input.itemIds.filter((id) => !present.has(id));
      if (missing.length > 0) batch = (await groomingApi.addBatchItems(batch.id, missing)).data;
      return batch;
    },
    retry: false,
    onSuccess: (batch) => {
      void queryClient.invalidateQueries({ queryKey: groomingKeys.batches(project) });
      queryClient.setQueryData(groomingKeys.batch(batch.id), { data: batch });
    },
  });
}

/** One held brief for Post all. */
export interface HeldBriefToPost {
  itemId: string;
  briefId: string;
}

/** The outcome of posting one held brief. */
export type PostAllResult =
  | { itemId: string; ok: true }
  | { itemId: string; ok: false; error: unknown; adoMessage?: string };

/**
 * Post all: post held briefs one by one, in order, as the signed-in user.
 * One failure never stops the rest; a failed post leaves its brief held
 * (the API keeps it held) and is reported with its error. Never retried
 * automatically: posting reaches the team.
 */
export function usePostHeldBriefs(project: string, batchId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      briefs,
      onResult,
    }: {
      briefs: HeldBriefToPost[];
      onResult?: (result: PostAllResult) => void;
    }): Promise<PostAllResult[]> => {
      const results: PostAllResult[] = [];
      for (const brief of briefs) {
        let result: PostAllResult;
        try {
          const response = await workItemsApi.postHeldBrief(
            { tracker: "ado", container: project, id: brief.itemId },
            brief.briefId
          );
          const comment = response.data.comment;
          result =
            comment.status === "failed"
              ? { itemId: brief.itemId, ok: false, error: null, ...(comment.error ? { adoMessage: comment.error.message } : {}) }
              : { itemId: brief.itemId, ok: true };
        } catch (error) {
          result = { itemId: brief.itemId, ok: false, error };
        }
        results.push(result);
        onResult?.(result);
      }
      return results;
    },
    retry: false,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: groomingKeys.batch(batchId) });
      void queryClient.invalidateQueries({ queryKey: workItemKeys.details() });
    },
  });
}

/** Refresh the grooming and item reads after the desktop app reported a change. */
export function useInvalidateGrooming() {
  const queryClient = useQueryClient();
  return (project: string) => {
    invalidateProject(queryClient, project);
    void queryClient.invalidateQueries({ queryKey: workItemKeys.all });
  };
}

// ---------------------------------------------------------------------------
// E280: the shared session agenda, meeting pattern and session date
// ---------------------------------------------------------------------------

/**
 * The project's next session (E280): the shared draft agenda (null when
 * nobody has started one), the meeting pattern, the next meeting date, the
 * session date and the typical brief run time. No tracker read.
 */
export function useProjectSession(project: string | undefined) {
  return useQuery({
    queryKey: groomingKeys.session(project ?? ""),
    queryFn: () => groomingApi.getProjectSession(project ?? ""),
    select: (response) => response.data,
    enabled: Boolean(project),
    retry: retryTransientOnce,
  });
}

/**
 * Refresh what a shared-agenda change touches: the project's session,
 * batches and backlog (all under the project key), the batch itself, and the
 * open item reads (an item's "in session" state).
 */
function invalidateSession(queryClient: ReturnType<typeof useQueryClient>, project: string, batchId?: string): void {
  invalidateProject(queryClient, project);
  if (batchId) void queryClient.invalidateQueries({ queryKey: groomingKeys.batch(batchId) });
  void queryClient.invalidateQueries({ queryKey: workItemKeys.details() });
}

/** The shared draft agenda's id, read fresh (never a stale cache: two people build it at once). */
async function sharedAgendaId(queryClient: ReturnType<typeof useQueryClient>, project: string): Promise<string | null> {
  const session = await queryClient.fetchQuery({
    queryKey: groomingKeys.session(project),
    queryFn: () => groomingApi.getProjectSession(project),
    staleTime: 0,
  });
  return session.data.batch?.id ?? null;
}

/**
 * Add items to the project's shared agenda (E280), creating it when there is
 * none. Any grooming user. The API also treats a create as "add to the
 * existing draft agenda", so two people adding at once still share one
 * agenda. Never retried.
 */
export function useAddToAgenda(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemIds: string[]): Promise<GroomingBatch> => {
      const agendaId = await sharedAgendaId(queryClient, project);
      if (agendaId) return (await groomingApi.addBatchItems(agendaId, itemIds)).data;
      return (await groomingApi.createBatch({ project, mode: "agenda", itemIds })).data.batch;
    },
    retry: false,
    onSuccess: (batch) => {
      invalidateSession(queryClient, project, batch.id);
    },
  });
}

/**
 * Remove an item from the project's shared agenda (E280). Pass the agenda's
 * `batchId` when the page has it; otherwise it is read from the session.
 * Any grooming user. Never retried.
 */
export function useRemoveFromAgenda(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId, batchId }: { itemId: string; batchId?: string }): Promise<GroomingBatch> => {
      const agendaId = batchId ?? (await sharedAgendaId(queryClient, project));
      if (!agendaId) throw new Error("This project has no session agenda yet");
      return (await groomingApi.removeBatchItem(agendaId, itemId)).data;
    },
    retry: false,
    onSuccess: (batch) => {
      invalidateSession(queryClient, project, batch.id);
    },
  });
}

/**
 * Set or clear (null) the shared draft agenda's session date, `YYYY-MM-DD`
 * (E280). Any grooming user; 409 once the agenda started. Never retried.
 */
export function useSetSessionDate(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, sessionDate }: { batchId: string; sessionDate: string | null }) =>
      groomingApi.patchBatchSessionDate(batchId, sessionDate).then((response) => response.data),
    retry: false,
    onSuccess: (batch) => {
      invalidateSession(queryClient, project, batch.id);
    },
  });
}

/**
 * Set or clear (null) the team's repeating grooming meeting (E280, board
 * auditors). Refreshes the config and the session (the next meeting date).
 * Never retried.
 */
export function useSetMeetingPattern(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (meetingPattern: GroomingMeetingPattern | null) =>
      groomingApi.putMeetingPattern(project, meetingPattern).then((response) => response.data),
    retry: false,
    onSuccess: () => {
      invalidateSession(queryClient, project);
    },
  });
}

// ---------------------------------------------------------------------------
// PKG-PM-C: the next meeting's agenda (carry over, and its undo)
// ---------------------------------------------------------------------------

/**
 * Carry a prepared agenda run's items to the next meeting's agenda
 * (`manage-agenda`, PKG-PM-C, frame C2's "Carry over N items"). Refreshes the
 * project's session and batches, the run and the agenda. Never retried; never
 * writes to ADO.
 */
export function useNextAgenda(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, itemIds }: { runId: string; itemIds: string[] }): Promise<NextAgendaResult> =>
      groomingApi.nextAgenda(runId, itemIds).then((response) => response.data),
    retry: false,
    onSuccess: (result, { runId }) => {
      invalidateSession(queryClient, project, runId);
      if (result.agenda) void queryClient.invalidateQueries({ queryKey: groomingKeys.batch(result.agenda.id) });
    },
  });
}

/**
 * Undo a carry-over (the toast's Undo, no confirm): remove the added items
 * from the agenda and cancel it when this carry-over created it and it is now
 * empty. Never retried.
 */
export function useUndoNextAgenda(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      agendaId,
      itemIds,
      cancelIfEmpty,
    }: {
      agendaId: string;
      itemIds: string[];
      cancelIfEmpty: boolean;
    }): Promise<UndoNextAgendaResult> =>
      groomingApi.undoNextAgenda(agendaId, { itemIds, cancelIfEmpty }).then((response) => response.data),
    retry: false,
    onSuccess: (_result, { agendaId }) => {
      invalidateSession(queryClient, project, agendaId);
    },
  });
}

/**
 * "Or pick one date" on Session before a schedule exists (PKG-PM-C, frame
 * 1e): start the project's shared draft agenda with no items and date it.
 * `POST /grooming/batches { project, mode: "agenda" }` returns the existing
 * shared draft when there is one, so two people never make two; the date is
 * the same `PATCH /grooming/batches/:id { sessionDate }` Change date sends
 * (`manage-agenda`). No change to the stored meeting pattern. Never retried.
 */
export function useCreateDatedAgenda(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionDate: string): Promise<GroomingBatch> => {
      const agendaId = await sharedAgendaId(queryClient, project);
      const id = agendaId ?? (await groomingApi.createBatch({ project, mode: "agenda" })).data.batch.id;
      return (await groomingApi.patchBatchSessionDate(id, sessionDate)).data;
    },
    retry: false,
    onSuccess: (batch) => {
      invalidateSession(queryClient, project, batch.id);
    },
  });
}

// ---------------------------------------------------------------------------
// FogBugz ↔ ADO project links (epic PI-SPEC-SW-E308, feature PI-SPEC-SW-1610)
// ---------------------------------------------------------------------------

/**
 * The project's FogBugz links (each with its resolved repos per target and
 * whether the caller may edit it) and the overrides of other projects' links
 * that point into it (`map-project`). Pass undefined to skip the read.
 */
export function useFogbugzLinks(project: string | undefined) {
  return useQuery({
    queryKey: groomingKeys.fogbugzLinks(project ?? ""),
    queryFn: () => groomingApi.fogbugzLinks(project ?? ""),
    select: (response) => response.data,
    enabled: Boolean(project),
    retry: retryTransientOnce,
  });
}

/**
 * Refresh every project's FogBugz-link list, not only the one edited: an
 * override shows under "Pointing here" on its target project too.
 */
function invalidateAllFogbugzLinks(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({
    queryKey: groomingKeys.all,
    predicate: (query) => query.queryKey[query.queryKey.length - 1] === "fogbugz-links",
  });
}

/** Link a FogBugz project to this ADO project. Never retried; refreshes every link list. */
export function useCreateFogbugzLink(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FogbugzProjectLinkInput) => groomingApi.createFogbugzLink(project, input).then((r) => r.data),
    retry: false,
    onSuccess: () => {
      invalidateAllFogbugzLinks(queryClient);
    },
  });
}

/** Replace a FogBugz link. Never retried; refreshes every link list. */
export function useUpdateFogbugzLink(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ linkId, input }: { linkId: string; input: FogbugzProjectLinkInput }) =>
      groomingApi.updateFogbugzLink(project, linkId, input).then((r) => r.data),
    retry: false,
    onSuccess: () => {
      invalidateAllFogbugzLinks(queryClient);
    },
  });
}

/** Remove a FogBugz link. Never retried; refreshes every link list. */
export function useDeleteFogbugzLink(project: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) => groomingApi.deleteFogbugzLink(project, linkId),
    retry: false,
    onSuccess: () => {
      invalidateAllFogbugzLinks(queryClient);
    },
  });
}

/**
 * An ADO project's area paths for the FogBugz link editor's picker (feature
 * PI-SPEC-SW-1644: `map-project`, the caller's own ADO PAT). Idle until
 * `enabled`; kept 5 minutes; a 4xx (no PAT, refused PAT, no role) is never
 * retried.
 */
export function useAdoAreaPaths(project: string | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: groomingKeys.adoAreaPaths(project ?? ""),
    queryFn: () => groomingApi.adoAreaPaths(project ?? ""),
    select: (response) => response.data,
    enabled: Boolean(project) && (options.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    retry: retryTransientOnce,
  });
}
